import { useQuery } from '@apollo/client';
import React, { useCallback, useState } from 'react';
import { RouteComponentProps } from 'react-router';
import Layout from '../../../../components/Layout';
import { MainColumn } from '../../../../components/Layout/ThirdPaneLayout';
import useNotify from '../../../../hooks/useNotify';
// import PropTypes from 'prop-types'
import { useHasPerm } from '@/hooks/usePerms';
import { ApolloError } from '@apollo/client/errors';
import { PortalWithState } from 'react-portal';
import { Flex, Text } from 'rebass';
import Accordion from '../../../../components/Accordion/Accordion';
import Address from '../../../../components/Address/Address';
import Button from '../../../../components/Button/Button';
import { CopyButton } from '../../../../components/CopyButton';
import CredentialEditModal from '../../../../components/Credential/CredentialEditModal';
import { Definitions } from '../../../../components/Definitions';
import GaugeEditModal from '../../../../components/Gauge/GaugeEditModal';
import PageBar from '../../../../components/PageBar/PageBar';
import {
  EmailInput,
  PhoneInput,
  ProfileType,
} from '../../../../components/Profile/types';
import DataTable from '../../../../components/Table/DataTable';
import credentialsColumns from '../../../../components/Tester/Credentials.columns';
import gaugesColumns from '../../../../components/Tester/Gauges.columns';
import {
  Address as AddressType,
  Credential,
  Gauge,
} from '../../../../components/Tester/types';
import SelfEditModal from '../../../../components/User/SelfEditModal';
import Perm from '../../../../types/perm';
import { GET_MY_TESTER_PROFILE } from './TesterProfile.query';
import { MyAccountSection, SecuritySection } from '@/components/Security';

type TesterProfileViewParams = Record<string, never>;

type TesterProfileViewProps = RouteComponentProps<TesterProfileViewParams>;

type Me = {
  id: string;
  type: ProfileType;
  name: string;
  firstName: string;
  lastName: string;
  contactAddressId: string | null;
  contactAddress: AddressType | null;
  phones: PhoneInput[];
  emails: EmailInput[];

  backflowTester: {
    gauges: Gauge[];
  };
  credentials: Credential[];

  perms: Perm[];

  user: {
    profileId: string;
    email: string;
  };
};

const TesterProfileView: React.FC<TesterProfileViewProps> = (props) => {
  const canUpdateProfile = useHasPerm('profiles', 'UPDATE');
  const canReadCredentials = useHasPerm('credentials', 'READ');
  const canCreateCredentials = useHasPerm('credentials', 'CREATE');
  const canUpdateCredentials = useHasPerm('credentials', 'UPDATE');
  const canReadGauges = useHasPerm('gauges', 'READ');
  const canCreateGauges = useHasPerm('gauges', 'CREATE');
  const canUpdateGauges = useHasPerm('gauges', 'UPDATE');
  const {
    loading: itemLoading,
    error: itemError,
    data: itemData,
    refetch,
  } = useQuery(GET_MY_TESTER_PROFILE, {
    // skip: !vendorId,
    // variables: { where: { id: vendorId || null } },
    onError: useCallback((error: ApolloError) => {
      showError(`Error loading tester profile`, error);
    }, []),
    fetchPolicy: 'network-only',
  });

  // TODO: handle error state
  // TODO: handle loading state

  const { showError } = useNotify();

  const profile = itemData?.me;
  const testerUser = profile?.user;

  // State
  const [selectedCredential, setSelectedCredential] = useState<Credential>();
  const [selectedGauge, setSelectedGauge] = useState<Gauge>();

  // Safety Cehck
  // How can we avoid this?
  if (!profile || !profile.backflowTester || !testerUser) {
    return (
      <Layout>
        <MainColumn px="mainContent" pb="mainContent" height="inherit">
          <PageBar title="Profile" />
          Loading...
        </MainColumn>
      </Layout>
    );
  }

  // Conditions
  const hasCredentials = !!profile?.credentials?.length;
  const hasGauges = !!profile?.backflowTester?.gauges?.length;

  // Destruct
  const {
    credentials,
    backflowTester: { gauges },
  } = profile;

  return (
    <Layout>
      <MainColumn px="mainContent" pb="mainContent" height="inherit">
        {/* This should get hoisted */}
        <PageBar title="Profile" />
        <Flex flexDirection="column" maxWidth={600}>
          <MyAccountSection />
          {/* Contact */}
          <Accordion
            title="Contact"
            variant="onMain"
            icon={
              canUpdateProfile ? (
                <PortalWithState closeOnEsc>
                  {({ openPortal, closePortal, portal }) => (
                    <>
                      <Button
                        variant="secondary"
                        onClick={openPortal}
                        icon="edit"
                      >
                        Edit
                      </Button>
                      {portal(
                        <SelfEditModal
                          onClose={closePortal}
                          profile={profile}
                          hide={['type', 'billingAddressId']}
                        />
                      )}
                    </>
                  )}
                </PortalWithState>
              ) : undefined
            }
          >
            <Definitions
              items={[
                {
                  key: `name`,
                  label: `Name`,
                  value: (
                    <>
                      {profile.name + ` `}
                      <CopyButton text={profile.name} label="Name" />
                    </>
                  ),
                },
                ...profile.phones.map((phone, i) => {
                  const label =
                    phone.subType === 'UNKNOWN'
                      ? `Phone ${i + 1}`
                      : `Phone (${phone.subType})`;

                  return {
                    key: `phone:${phone.id}`,
                    label,
                    value: (
                      <>
                        {phone.value + ` `}
                        <CopyButton text={phone.value} label={label} />
                      </>
                    ),
                  };
                }),
                ...profile.emails.map((email) => {
                  return {
                    key: `email:${email.id}`,
                    label: `Email`,
                    value: (
                      <>
                        {email.value + ` `}
                        <CopyButton text={email.value} label="Email" />
                      </>
                    ),
                  };
                }),
                ...(profile.contactAddress
                  ? [
                      {
                        key: `address`,
                        label: `Mailing Address`,
                        value: <Address address={profile.contactAddress} />,
                      },
                    ]
                  : []),
              ]}
            />
          </Accordion>
          {/* Creds */}
          <Accordion
            title="Credentials"
            variant="onMain"
            icon={
              canCreateCredentials ? (
                <PortalWithState closeOnEsc>
                  {({ openPortal, closePortal, portal }) => (
                    <>
                      <Button
                        variant="primary"
                        icon="plus"
                        onClick={openPortal}
                      >
                        Credential
                      </Button>
                      {portal(
                        <CredentialEditModal
                          action="create"
                          credential={{
                            profileId: profile.id,
                            type: 'BACKFLOW',
                          }}
                          onClose={() => {
                            refetch();
                            closePortal();
                          }}
                        />
                      )}
                    </>
                  )}
                </PortalWithState>
              ) : undefined
            }
          >
            {hasCredentials && canReadCredentials && (
              <PortalWithState closeOnEsc>
                {({ openPortal, closePortal, portal }) => (
                  <>
                    <DataTable
                      columns={credentialsColumns}
                      items={credentials}
                      onRowClick={(credential) => {
                        setSelectedCredential(credential);
                        openPortal();
                      }}
                    />
                    {portal(
                      <>
                        {selectedCredential && (
                          <CredentialEditModal
                            action="edit"
                            credential={{
                              ...selectedCredential,
                              profileId: profile.id,
                            }}
                            readOnly={!canUpdateCredentials}
                            onClose={() => {
                              refetch();
                              closePortal();
                            }}
                          />
                        )}
                      </>
                    )}
                  </>
                )}
              </PortalWithState>
            )}
            {!hasCredentials && (
              <Text variant="body2" color="fieldBorderHover">
                There are no credentials yet.
              </Text>
            )}
          </Accordion>

          {/* Gauges */}
          <Accordion
            variant="onMain"
            title="Gauges"
            icon={
              canCreateGauges ? (
                <PortalWithState closeOnEsc>
                  {({ openPortal, closePortal, portal }) => (
                    <>
                      <Button
                        variant="primary"
                        icon="plus"
                        onClick={openPortal}
                      >
                        Gauge
                      </Button>
                      {portal(
                        <GaugeEditModal
                          action="create"
                          gauge={{
                            profileId: profile.id,
                          }}
                          onClose={() => {
                            refetch();
                            closePortal();
                          }}
                        />
                      )}
                    </>
                  )}
                </PortalWithState>
              ) : undefined
            }
          >
            {hasGauges && canReadGauges && (
              <PortalWithState closeOnEsc>
                {({ openPortal, closePortal, portal }) => (
                  <>
                    <DataTable
                      columns={gaugesColumns}
                      items={gauges}
                      onRowClick={(gauge) => {
                        setSelectedGauge(gauge);
                        openPortal();
                      }}
                    />
                    {portal(
                      <>
                        {selectedGauge && (
                          <GaugeEditModal
                            action="edit"
                            gauge={{
                              ...selectedGauge,
                              profileId: profile.id,
                            }}
                            readOnly={!canUpdateGauges}
                            onClose={() => {
                              refetch();
                              closePortal();
                            }}
                          />
                        )}
                      </>
                    )}
                  </>
                )}
              </PortalWithState>
            )}
            {!hasGauges && (
              <Text variant="body2" color="fieldBorderHover">
                There are no gauges yet.
              </Text>
            )}
          </Accordion>
          <SecuritySection />
        </Flex>
      </MainColumn>
    </Layout>
  );
};

export default TesterProfileView;
